import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { SimpleGrid, Stack } from '@chakra-ui/react'
import { RadioCardGroup } from '/overrides/app/components/radio-card'
import { useForm, Controller } from 'react-hook-form'
import { useShopperCustomersMutation } from '@salesforce/commerce-sdk-react'
import BillingAddressForm from './billing-address-form'
import BillingAddressCard from '../shared/address-card'
import AddNewBillingAddressButton from '../shared/add-new-address-button'
import {
    DEFAULT_BILLING_VALUES,
    transformToBillingFormat,
    findMatchingAddress
} from '../shared/form-constants'
import {useTranslationHelpers} from '@epmc-mach/accelarator/overrides/app/hooks'
import {useCustomerLocation} from '@epmc-mach/accelarator/overrides/app/contexts/CustomerLocation'
import {Heading} from '@salesforce/retail-react-app/app/components/shared/ui'
import { useCurrentCustomer } from '@epmc-mach/accelarator/overrides/app/hooks/use-current-customer'

const BillingAddressSelection = ({
    onAddressSelect,
    selectedAddress,
    billingForm,
    setIsEditingAddress,
    isEditingAddress,
    noBillingSelected
}) => {
    const {data: customer} = useCurrentCustomer();
    const [editingAddressId, setEditingAddressId] = useState(null)
    const {tShipping, tFormLabels} = useTranslationHelpers()
    const {defaultCountryCode} = useCustomerLocation()
    const removeCustomerAddress = useShopperCustomersMutation('removeCustomerAddress')

    const hasSavedAddresses = customer?.addresses?.length > 0

    const matchedAddress =
        customer?.addresses?.find((addr) => addr.c_isBillingAddress) ||
        findMatchingAddress(selectedAddress, customer?.addresses) ||
        (selectedAddress
            ? customer?.addresses?.find(
                (addr) =>
                    addr.address1 === selectedAddress.address1 &&
                    addr.city === selectedAddress.city
            )
            : null)

    // Form for managing address selection
    const selectionForm = useForm({
        mode: 'onChange',
        defaultValues: {
            addressId: matchedAddress?.addressId || ''
        }
    })

    // Update form when matched address changes
    useEffect(() => {
        if (matchedAddress?.addressId) {
            selectionForm.setValue('addressId', matchedAddress.addressId)
        }
        if (noBillingSelected) {
            onAddressSelect(matchedAddress)
        }
    }, [matchedAddress?.addressId, selectionForm])

    useEffect(() => {
        if (!isEditingAddress) {
            setEditingAddressId(null)
        }
    }, [isEditingAddress])

    const handleAddressSelect = (addressId) => {
        selectionForm.setValue('addressId', addressId)
        setIsEditingAddress(false)
        setEditingAddressId(null)
        const address = customer?.addresses?.find((addr) => addr.addressId === addressId)
        if (address && onAddressSelect) {
            onAddressSelect(address)
        }
    }

    const handleEdit = (address) => {
        setEditingAddressId(address.addressId)
        selectionForm.setValue('addressId', address.addressId)
        setIsEditingAddress(true)
    }

    const handleNew = () => {
        setEditingAddressId(null)
        selectionForm.setValue('addressId', '')
        setIsEditingAddress(true)
    }

    const handleCancel = () => {
        setIsEditingAddress(false)
        setEditingAddressId(null)
    }

    const handleRemove = async (addressId) => {
        const currentSelected = selectionForm.getValues('addressId')
        if (addressId === currentSelected) {
            selectionForm.setValue('addressId', '')
        }

        await removeCustomerAddress.mutateAsync({
            parameters: {
                customerId: customer.customerId,
                addressName: addressId
            }
        })

        // If last address removed, show form
        if (customer?.addresses?.length === 1) {
            setIsEditingAddress(true)
        }
    }

    const getEditingAddress = () => {
        if (!editingAddressId) return null
        return customer?.addresses?.find((addr) => addr.addressId === editingAddressId)
    }

    // Get form values for external save button
    useEffect(() => {
        if (billingForm && isEditingAddress) {
            const address = getEditingAddress()
            if (address) {
                // Edit mode - fill form with billing_ prefix
                const formData = transformToBillingFormat(address)
                billingForm.reset(formData)
            } else {
                // New address mode
                billingForm.reset({
                    ...DEFAULT_BILLING_VALUES,
                    billing_countryCode: defaultCountryCode
                })
            }
        }
    }, [isEditingAddress, editingAddressId, billingForm])

    const selectedAddressId = selectionForm.watch('addressId')

    return (
        <Stack spacing={4}>
            {hasSavedAddresses && (
                <>
                    <Heading
                        fontSize="15.5px"
                        lineHeight="30px"
                        letterSpacing="0"
                        color={'gray.600'}
                        tabIndex="0"
                    >
                        {tFormLabels?.billingAddressLabel}
                    </Heading>
                    <Controller
                        name="addressId"
                        control={selectionForm.control}
                        defaultValue=""
                        render={({field: {value}}) => (
                            <RadioCardGroup
                                value={value}
                                onChange={handleAddressSelect}
                                name="billingAddressId"
                            >
                                <SimpleGrid
                                    columns={[1, 1, 2]}
                                    spacing={4}
                                    gridAutoFlow="row dense"
                                >
                                    {customer.addresses.map((address, index) => (
                                        <BillingAddressCard
                                            key={address.addressId}
                                            address={address}
                                            index={index}
                                            onEdit={handleEdit}
                                            onRemove={handleRemove}
                                            isEditingAddress={isEditingAddress}
                                            editingAddressId={editingAddressId}
                                            selectedAddressId={selectedAddressId}
                                            type="billing"
                                            EditFormComponent={
                                                <BillingAddressForm
                                                    address={address}
                                                    onCancel={handleCancel}
                                                    hasSavedAddresses={hasSavedAddresses}
                                                    form={billingForm}
                                                />
                                            }
                                        />
                                    ))}

                                    <AddNewBillingAddressButton onClick={handleNew}>
                                        {tShipping?.linkAddNewAddress}
                                    </AddNewBillingAddressButton>
                                </SimpleGrid>
                            </RadioCardGroup>
                        )}
                    />
                </>
            )}

            {(customer?.isGuest || (customer?.isRegistered && !hasSavedAddresses) || (isEditingAddress && !editingAddressId)) && (
                <BillingAddressForm
                    address={null}
                    onCancel={handleCancel}
                    hasSavedAddresses={hasSavedAddresses}
                    form={billingForm}
                />
            )}
        </Stack>
    )
}

BillingAddressSelection.propTypes = {
    onAddressSelect: PropTypes.func,
    selectedAddress: PropTypes.object,
    billingForm: PropTypes.object
}

export default BillingAddressSelection
